import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Empty, Grid, Input, Modal, Spin, Typography } from "antd";
import type { InputRef } from "antd";
import { Link, useNavigate } from "react-router-dom";
import useDebounce from "../../hooks/useDebounce";
import { globalSearch } from "../../services/search/search.service";
import { SearchResultGroup } from "../../services/search/search.types";
import { ENTITY_GROUP_LABEL, flattenResults, getNavigationPath } from "./utils";
import PreviewPanel from "./PreviewPanel";

const { Text } = Typography;
const { useBreakpoint } = Grid;

// Results list scroll height, shared with the preview column so both sides
// stay the same height.
const PANEL_MAX_HEIGHT = 400;

// A query counts as searchable when it has at least 2 characters, or when
// it's purely numeric (mirrors the backend gate: "queries de <2 caracteres
// no numéricos devuelven vacío").
const isSearchableQuery = (trimmed: string): boolean =>
  trimmed.length >= 2 || /^[0-9]+$/.test(trimmed);

const CommandPalette: React.FC = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResultGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Lets us restore focus to the search input after a modifier-click on a
  // result (which keeps the palette open) so keyboard nav keeps working.
  const inputRef = useRef<InputRef>(null);

  const debouncedQuery = useDebounce(query, 300);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setResults([]);
    setActiveIndex(0);
  }, []);

  // Global shortcut: Cmd+K (Mac) / Ctrl+K opens the palette; Escape closes it.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") {
        close();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [close]);

  // The Header's search button has no shared context with this component,
  // so it opens the palette by dispatching this CustomEvent on window instead.
  useEffect(() => {
    const handleOpenEvent = () => setOpen(true);
    window.addEventListener("open-command-palette", handleOpenEvent);
    return () =>
      window.removeEventListener("open-command-palette", handleOpenEvent);
  }, []);

  // Tracks the most recently requested search so a slow, stale response
  // can't clobber results from a query typed afterwards (same pattern as
  // ContentMap/index.tsx's fetchTree).
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!isSearchableQuery(trimmed)) {
      latestRequestIdRef.current += 1; // invalidate any in-flight request
      setResults([]);
      setLoading(false);
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    setLoading(true);
    globalSearch(trimmed)
      .then((response) => {
        if (requestId !== latestRequestIdRef.current) return;
        // Silently ignore any group the UI has no label/route for (an
        // entity this UI version doesn't know about yet).
        const knownResults = response.results.filter(
          (group) => ENTITY_GROUP_LABEL[group.entity] !== undefined
        );
        setResults(knownResults);
        setActiveIndex(0);
      })
      .catch(() => {
        if (requestId !== latestRequestIdRef.current) return;
        setResults([]);
      })
      .finally(() => {
        if (requestId === latestRequestIdRef.current) setLoading(false);
      });
  }, [debouncedQuery]);

  const flatResults = useMemo(() => flattenResults(results), [results]);

  const goToResult = useCallback(
    (entity: SearchResultGroup["entity"], id: number) => {
      navigate(getNavigationPath(entity, id));
      close();
    },
    [navigate, close]
  );

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (flatResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % flatResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + flatResults.length) % flatResults.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const active = flatResults[activeIndex];
      if (active) goToResult(active.entity, active.id);
    }
  };

  // Derived from the debounced value (not the raw input) so this stays in
  // sync with `results`, which only updates once the debounce settles —
  // otherwise clearing the input would flash the help text while the
  // previous query's results are still on screen.
  const trimmedDebouncedQuery = debouncedQuery.trim();
  const showEmpty =
    !loading &&
    isSearchableQuery(trimmedDebouncedQuery) &&
    flatResults.length === 0;
  const showHelp = !loading && !isSearchableQuery(trimmedDebouncedQuery);

  // Only reserve room for the preview column on md+ viewports; on narrow
  // screens the modal collapses back to a single column so the layout can't
  // overflow horizontally.
  const screens = useBreakpoint();
  const showPreview = !!screens.md;

  // The item under the cursor/keyboard drives the preview; null when there
  // is no active result (short query, no matches, etc.).
  const activeItem = flatResults[activeIndex];
  const previewEntity = activeItem ? activeItem.entity : null;
  const previewId = activeItem ? activeItem.id : null;

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      title={null}
      closable={false}
      centered={false}
      width={showPreview ? 880 : 560}
      style={{ top: 80 }}
      destroyOnClose
    >
      <Input
        ref={inputRef}
        autoFocus
        allowClear
        size="large"
        placeholder="Buscar por ID o nombre… (programas, cursos, recursos, usuarios…)"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleInputKeyDown}
      />

      <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
        <div
          style={{
            ...(showPreview
              ? { width: 520, flexShrink: 0 }
              : { flex: 1, minWidth: 0 }),
            maxHeight: PANEL_MAX_HEIGHT,
            overflowY: "auto",
          }}
        >
          {loading && (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Spin size="small" />
            </div>
          )}

          {showHelp && <Text type="secondary">Escribe un ID o un nombre…</Text>}

          {showEmpty && <Empty description="Sin resultados" />}

          {!loading &&
            results.map((group) => (
              <div key={group.entity} style={{ marginBottom: 12 }}>
                <Text
                  type="secondary"
                  style={{
                    textTransform: "uppercase",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {ENTITY_GROUP_LABEL[group.entity]}
                </Text>
                {group.items.map((item) => {
                  const key = `${group.entity}-${item.id}`;
                  const flatIndex = flatResults.findIndex((r) => r.key === key);
                  const isActive = flatIndex === activeIndex;
                  return (
                    // A real <Link> (renders an <a href>) so middle-click and
                    // Cmd/Ctrl+click open the result in a new tab natively.
                    // A plain left-click still navigates in-app and closes the
                    // palette; modified clicks keep it open so several results
                    // can be opened in new tabs. Keyboard nav (arrows + Enter)
                    // is unaffected — focus stays on the search input.
                    <Link
                      key={key}
                      to={getNavigationPath(group.entity, item.id)}
                      onClick={(e) => {
                        if (
                          e.metaKey ||
                          e.ctrlKey ||
                          e.shiftKey ||
                          e.altKey ||
                          e.button !== 0
                        ) {
                          // The palette stays open so several results can be
                          // opened in new tabs; keep the search input focused
                          // so ↑/↓ keyboard nav keeps working.
                          inputRef.current?.focus();
                          return;
                        }
                        close();
                      }}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "6px 8px",
                        borderRadius: 4,
                        cursor: "pointer",
                        color: "inherit",
                        textDecoration: "none",
                        backgroundColor: isActive
                          ? "var(--bo-inset-strong-bg)"
                          : "transparent",
                      }}
                    >
                      <span>
                        {item.title}
                        {item.subtitle && (
                          <Text type="secondary" style={{ marginLeft: 8 }}>
                            {item.subtitle}
                          </Text>
                        )}
                      </span>
                      <Text type="secondary">#{item.id}</Text>
                    </Link>
                  );
                })}
              </div>
            ))}
        </div>

        {showPreview && (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              maxHeight: PANEL_MAX_HEIGHT,
              overflowY: "auto",
              borderLeft: "1px solid var(--bo-card-border)",
              background: "var(--bo-inset-bg)",
              borderRadius: 8,
              paddingLeft: 16,
            }}
          >
            <PreviewPanel
              entity={previewEntity}
              id={previewId}
              onNavigate={close}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CommandPalette;
